import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import StudentAbsenceCard from './StudentAbsenceCard';
import { StudentAbsenceSummary } from '../../../../types/absenceTypes';
import SkeletonLoader from '../../../../components/Common/SkeletonLoader';
import AntPagination from '../../../../components/Common/ui/Pagination';
import { getStudentAbsenceSummaries } from '../../../../services/absenceService';
import '../../../../components/Common/styles/skeleton.css';
import CommonSelect from 'components/Common/CommonSelectAntd';

type SortByType = 'totalAbsenceHours' | 'percentageOfThreshold' | 'name' | 'class';

interface StudentAbsenceGridProps {
  // No props needed as component will manage its own state
}

const StudentAbsenceGrid: React.FC<StudentAbsenceGridProps> = () => {
  // Internal state management
  const [students, setStudents] = useState<StudentAbsenceSummary[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [sortBy, setSortBy] = useState<SortByType>('totalAbsenceHours');
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 6,
    totalPages: 0
  });
  const [error, setError] = useState<string | null>(null);
  
  const { t } = useTranslation();

  // Fetch student data
  const fetchStudents = async (page: number, limit: number, sortOption: SortByType) => {
    try {
      setLoading(true);
      const response = await getStudentAbsenceSummaries(page, limit, sortOption);
      setStudents(response.data);
      setPagination(response.pagination);
      setError(null);
    } catch (err) {
      setError('Failed to fetch student absence summaries');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Initial data fetch
  useEffect(() => {
    fetchStudents(pagination.page, pagination.limit, sortBy);
  }, []);
  
  // Handle page change
  const handlePageChange = (page: number) => {
    fetchStudents(page, pagination.limit, sortBy);
  };

  // Handle limit change
  const handleLimitChange = (limit: number) => {
    fetchStudents(1, limit, sortBy);
  };

  // Handle sort change
  const handleSortChange = (newSortBy: SortByType) => {
    setSortBy(newSortBy);
    fetchStudents(pagination.page, pagination.limit, newSortBy);
  };

  if (loading) {
    return (
      <div className="content-fade-in">
        <div className="card mb-4 skeleton-list-item">
          <div className="card-body d-flex justify-content-between align-items-center">
            <div>
              <SkeletonLoader type="text" width="150px" height="24px" variant="large" className="mb-1" />
              <SkeletonLoader type="text" width="200px" height="16px" variant="small" />
            </div>
            <div className="d-flex">
              <SkeletonLoader type="button" width="120px" height="38px" className="mr-3" />
              <SkeletonLoader type="button" width="120px" height="38px" />
            </div>
          </div>
        </div>
        
        <div className="row">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="col-md-4 col-sm-6 mb-4" style={{ '--item-index': i } as React.CSSProperties}>
              <div className="card skeleton-list-item">
                <div className="card-body">
                  <div className="d-flex align-items-center mb-3">
                    <div className="mr-3">
                      <SkeletonLoader type="circle" width="50px" height="50px" index={i} />
                    </div>
                    <div>
                      <SkeletonLoader type="text" width="120px" height="20px" className="mb-1" index={i} />
                      <SkeletonLoader type="text" width="80px" height="16px" variant="small" index={i} />
                    </div>
                  </div>
                  
                  <div className="d-flex justify-content-between mb-2">
                    <SkeletonLoader type="text" width="80px" height="16px" index={i} />
                    <SkeletonLoader type="text" width="30px" height="16px" index={i} />
                  </div>
                  
                  <SkeletonLoader type="progress" width="100%" height="8px" className="mb-3" index={i} />
                  
                  <div className="d-flex justify-content-between small">
                    <SkeletonLoader type="text" width="80px" height="16px" variant="small" index={i} />
                    <SkeletonLoader type="text" width="80px" height="16px" variant="small" index={i} />
                  </div>
                  
                  <div className="mt-3 pt-2 border-top">
                    <div className="d-flex justify-content-between align-items-center">
                      <SkeletonLoader type="text" width="70px" height="16px" variant="small" index={i} />
                      <SkeletonLoader type="badge" width="40px" height="24px" index={i} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (students.length === 0) {
    return (
      <div className="card">
        <div className="card-body text-center py-5">
          <i className="ti ti-user-check text-success ti-3x mb-3"></i>
          <h5>{t('no_absences_found')}</h5>
          <p className="text-muted">{t('no_absence_records_message')}</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="card mb-4">
        <div className="card-body d-flex justify-content-between align-items-center">
          <div>
            <h5 className="mb-0">{t('student_absences')}</h5>
            <small className="text-muted">
              {t('showing')} {(pagination.page - 1) * pagination.limit + 1} -{' '}
              {Math.min(pagination.page * pagination.limit, pagination.total)} {t('of')}{' '}
              {pagination.total} {t('students')}
            </small>
          </div>
          <div className="d-flex">
            <div className="form-group mr-3 mb-0">
              <CommonSelect
                className="mb-2"
                value={sortBy}
                onChange={(val) => handleSortChange(val as SortByType)}
                options={[
                  { value: 'percentageOfThreshold', label: t('sort_by_threshold') },
                  { value: 'totalAbsenceHours',       label: t('sort_by_total_absence_hours') },
                  { value: 'name',                    label: t('sort_by_name') },
                  { value: 'class',                   label: t('sort_by_class') },
                ]}
                placeholder={t('sort_by')}
              />
            </div>
            <div className="form-group mb-0">
              <CommonSelect
                value={pagination.limit.toString()}
                onChange={(val) => handleLimitChange(parseInt(val))}
                options={[
                  { value: '6',  label: `6 ${t('per_page')}` },
                  { value: '12', label: `12 ${t('per_page')}` },
                  { value: '24', label: `24 ${t('per_page')}` },
                ]}
                placeholder={t('per_page')}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        {students.map((student) => (
          <div key={student.student.id} className="col-md-4 col-sm-6 mb-4">
            <StudentAbsenceCard student={student} />
          </div>
        ))}
      </div>

      <AntPagination
        currentPage={pagination.page}
        totalItems={pagination.total}
        pageSize={pagination.limit}
        onPageChange={handlePageChange}
        onPageSizeChange={handleLimitChange}
        pageSizeOptions={[6, 12, 24, 48]}
      />
    </div>
  );
};

export default StudentAbsenceGrid;
